// Editor de um minicurso: cabeçalho com status, avisos de qualidade e as abas
// Etapas, Prévia (ao lado das etapas em telas largas) e Configurações.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiAlertOctagon, FiAlertTriangle, FiArrowLeft, FiInfo } from 'react-icons/fi';
import Carregando from '../Carregando';
import { SeloStatus } from '../relatorios/Graficos';
import ConfigCurso from './ConfigCurso';
import EtapasCurso, { RASCUNHO_VAZIO, aplicarRascunho } from './EtapasCurso';
import { seloDoCurso } from './ListaCursos';
import PreviaCurso from './PreviaCurso';
import { useEtapasDoCurso } from './useDadosMinicursos';
import { avisosDoCurso, configDoCurso, cursoAtivo, ehQuiz, formatarIntervalo, plural, temaDoCurso } from './util';

const LARGURA_LADO_A_LADO = 860;
const ICONE_AVISO = { perigo: FiAlertOctagon, atencao: FiAlertTriangle, info: FiInfo };

function useLargura() {
  const [largura, setLargura] = useState(0);
  const observador = useRef(null);
  const ref = useCallback((el) => {
    if (observador.current) observador.current.disconnect();
    if (!el) return;
    setLargura(el.getBoundingClientRect().width);
    observador.current = new ResizeObserver(([e]) => setLargura(e.contentRect.width));
    observador.current.observe(el);
  }, []);
  useLayoutEffect(() => () => observador.current && observador.current.disconnect(), []);
  return [ref, largura];
}

function EditorCurso({
  curso, cursos, tenantId, dados, andamento, aba, onAba, onVoltar, onAlternarStatus, sujoRef,
}) {
  const { temas, pilulas, questoes, conteudoDe, registrarEtapas } = dados;
  const salvas = useEtapasDoCurso(curso.id, conteudoDe);
  const [rascunho, setRascunho] = useState(RASCUNHO_VAZIO);
  const [configSujo, setConfigSujo] = useState(false);
  const [refLargura, largura] = useLargura();
  const largo = largura >= LARGURA_LADO_A_LADO;

  useEffect(() => { if (salvas) registrarEtapas(salvas); }, [salvas, registrarEtapas]);

  const { ativas } = useMemo(() => aplicarRascunho(salvas || [], rascunho), [salvas, rascunho]);
  const rascunhoSujo = useMemo(() => {
    if (!salvas) return false;
    if (rascunho.removidas.length) return true;
    return ativas.some((e, i) => salvas[i]?.id !== e.id);
  }, [salvas, rascunho, ativas]);
  const sujo = rascunhoSujo || configSujo;

  // avisa antes de fechar a aba do navegador com alterações pendentes
  useEffect(() => {
    sujoRef.current = sujo;
    if (!sujo) return undefined;
    const aviso = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [sujo, sujoRef]);
  useEffect(() => () => { sujoRef.current = false; }, [sujoRef]);

  const avisos = useMemo(() => (salvas ? avisosDoCurso(ativas, curso) : []), [salvas, ativas, curso]);
  const nQuiz = ativas.filter((e) => e.conteudo && ehQuiz(e)).length;
  const { intervalo, aprovacao, lembrete } = configDoCurso(curso);
  const tema = temaDoCurso(curso, temas);

  const abas = [
    { id: 'etapas', rotulo: `Etapas${salvas ? ` (${ativas.length})` : ''}` },
    ...(largo ? [] : [{ id: 'previa', rotulo: 'Prévia' }]),
    { id: 'config', rotulo: `Configurações${configSujo ? ' •' : ''}` },
  ];
  const abaAtual = abas.some((a) => a.id === aba) ? aba : 'etapas';

  // setas esquerda/direita trocam de aba (padrão de tablist)
  const teclaNasAbas = (e) => {
    const i = abas.findIndex((a) => a.id === abaAtual);
    const passo = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!passo) return;
    e.preventDefault();
    const proxima = abas[(i + passo + abas.length) % abas.length];
    onAba(proxima.id);
    requestAnimationFrame(() => document.getElementById(`mc-aba-${proxima.id}`)?.focus());
  };

  const previa = salvas && <PreviaCurso curso={curso} etapas={ativas} alterada={rascunhoSujo} />;

  return (
    <div className="mc-pagina mc-editor" ref={refLargura}>
      <button type="button" className="btn-ghost mc-botao mc-voltar" onClick={onVoltar}>
        <FiArrowLeft aria-hidden="true" /> Todos os cursos
      </button>

      <header className="mc-editor__cabecalho">
        <div className="mc-editor__identidade">
          <div className="mc-editor__titulo-linha">
            <h1 className="mc-titulo">{curso.nome || '(sem nome)'}</h1>
            <SeloStatus status={seloDoCurso(curso)} />
          </div>
          <p className="mc-curso__meta">
            <code className="mc-comando">{curso.comando}</code>
            <span>a cada {formatarIntervalo(intervalo)}</span>
            <span>aprovação {aprovacao}%</span>
            <span>{lembrete ? `lembrete após ${lembrete} h` : 'sem lembrete'}</span>
            {tema && <span>tema {tema.nome}</span>}
            <span>
              {!andamento.length && 'ninguém em andamento'}
              {andamento.length > 0 && (cursoAtivo(curso)
                ? `${plural(andamento.length, 'aluno', 'alunos')} em andamento`
                : plural(andamento.length, 'aluno pausado', 'alunos pausados'))}
            </span>
          </p>
        </div>
        <button
          type="button"
          className={`${cursoAtivo(curso) ? 'btn-secondary' : 'btn-primary'} mc-botao`}
          onClick={() => onAlternarStatus(curso, salvas)}
          disabled={!salvas}
        >
          {cursoAtivo(curso) ? 'Desativar curso' : 'Ativar curso'}
        </button>
      </header>

      {avisos.length > 0 && (
        <section className="mc-avisos" aria-label="Avisos do curso">
          <ul>
            {avisos.map((a) => {
              const Icone = ICONE_AVISO[a.nivel];
              return (
                <li key={a.id} className={`mc-aviso mc-aviso--${a.nivel}`}>
                  <Icone aria-hidden="true" />
                  <span>{a.texto}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="mc-abas" role="tablist" aria-label="Partes do curso" onKeyDown={teclaNasAbas}>
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            id={`mc-aba-${a.id}`}
            aria-selected={abaAtual === a.id}
            aria-controls={`mc-painel-${a.id}`}
            tabIndex={abaAtual === a.id ? 0 : -1}
            className={`mc-aba${abaAtual === a.id ? ' is-ativa' : ''}`}
            onClick={() => onAba(a.id)}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div id="mc-painel-etapas" role="tabpanel" aria-labelledby="mc-aba-etapas" hidden={abaAtual !== 'etapas'}>
        {!salvas ? <Carregando texto="Carregando etapas..." /> : (
          <EtapasCurso
            curso={curso}
            tenantId={tenantId}
            salvas={salvas}
            rascunho={rascunho}
            setRascunho={setRascunho}
            andamento={andamento}
            temaCursoId={tema?.id || ''}
            pilulas={pilulas}
            questoes={questoes}
            temas={temas}
            lateral={largo ? previa : null}
          />
        )}
      </div>

      {!largo && (
        <div id="mc-painel-previa" role="tabpanel" aria-labelledby="mc-aba-previa" hidden={abaAtual !== 'previa'}>
          {abaAtual === 'previa' && (previa || <Carregando texto="Carregando etapas..." />)}
        </div>
      )}

      <div id="mc-painel-config" role="tabpanel" aria-labelledby="mc-aba-config" hidden={abaAtual !== 'config'}>
        <ConfigCurso
          curso={curso}
          cursos={cursos}
          temas={temas}
          nQuiz={nQuiz}
          andamento={andamento.length}
          onSujo={setConfigSujo}
        />
      </div>
    </div>
  );
}

export default EditorCurso;
